<template>
    <h4 class="link-header mb-md" id="products">Mapped Products</h4>
    <div class="patent-mapped-products display-flex w-100 mb-md flex-wrap">
        <div class="run-sim-button flex-grow-1">
            <div class="card-content h-100 w-100">
                <div
                    v-if="
                        canShowProducts &&
                        !loadingProducts &&
                        productList.length === 0 &&
                        !noProducts
                    "
                    class="cta h-100 display-flex align-items-center justify-center flex-wrap"
                >
                    <p class="large semi-bold w-100 text-center">
                        Where does this patent fit in the product landscape?
                    </p>
                    <AonButton
                        class="mt-n8"
                        label="Run Product Mapping"
                        @clicked="onRunProductMapping"
                    />
                </div>
                <div
                    v-if="!loadingProducts && (noProducts || !canShowProducts)"
                    class="no-patents h-100 display-flex align-items-center justify-center flex-wrap"
                >
                    <p class="large semi-bold w-100 text-center">{{ noProductsMessage }}</p>
                </div>
                <AonCoverLoading :loading="loadingProducts" title="Mapping Products..." />
            </div>
        </div>
        <AonCard v-if="productList.length > 0" class="w-100 mb-sm">
            <div class="chart-holder">
                <div id="productMapChart" :style="{ height: chartHeight + 'px' }"></div>
                <div class="legend display-flex align-items-center">
                    <div class="target display-flex align-items-center mr-md">
                        <div class="indicator mr-sm"></div>
                        <p class="small">This Patent</p>
                    </div>
                </div>
            </div>
        </AonCard>
    </div>
</template>

<script setup>
import {
    ref,
    inject,
    watch,
    nextTick,
    onMounted,
    onBeforeUnmount,
    defineExpose,
    computed,
} from 'vue'
import { runProductMapping } from '@/api/patentViewer'
import {
    useEntityStore,
    usePatentViewerStore,
    usePlaybookGenerationStore,
    useReportGenerationStore,
    useArenaOutputStore,
} from '@/stores'

import * as am5 from '@amcharts/amcharts5'
import { useViolinChart } from '@/composables/charts/violinChart'

import { config } from '@/config'
import { debounce } from 'lodash-es'

const logger = inject('logger')
const filters = inject('filters')
const eventBus = inject('eventBus')

const entityStore = useEntityStore()
const patentViewerStore = usePatentViewerStore()
const playbookGenerationStore = usePlaybookGenerationStore()
const reportGenerationStore = useReportGenerationStore()
const arenaOutputStore = useArenaOutputStore()

const props = defineProps({
    patentDetails: {
        type: Object,
        default: () => ({}),
    },
    loadingPatentDetail: {
        type: Boolean,
        default: false,
    },
})

const loadingProducts = ref(false)
const productList = ref([])
const noProducts = ref(false)
const chartData = ref(null)
const targetItem = ref(null)
const currentPatentId = ref(null)

const chartHeight = computed(() => {
    const baseHeight = 150
    const heightPerProduct = 50
    const calculatedHeight = baseHeight + productList.value.length * heightPerProduct

    return Math.max(calculatedHeight, 150)
})

const canShowProducts = computed(() => {
    const patentDetails = patentViewerStore.patentDetails
    return patentDetails?.status === 'Active' && patentDetails?.patentId.startsWith('US-')
})

const noProductsMessage = computed(() => {
    return !canShowProducts.value
        ? 'Products can only be shown for US, Active patents currently'
        : 'No products identified'
})

let root

const debouncedRunProductMapping = debounce(async (patentId, entityPk) => {
    await fetchProductMapping(patentId, entityPk)
}, 300)

const checkCacheAndLoadData = (patentId) => {
    if (!patentId) return

    // Check if we have cached data
    if (patentViewerStore.mappedProductsExistInCache(patentId)) {
        const cachedData = patentViewerStore.getCachedMappedProducts(patentId)
        productList.value = cachedData

        if (productList.value.length === 0) {
            noProducts.value = true
        } else {
            nextTick(() => {
                mapDataToChart()
            })
        }
    }
}

onMounted(() => {
    eventBus.on('patent-changed', handlePatentChanged)

    if (props.patentDetails?.publicationPk) {
        checkCacheAndLoadData(props.patentDetails.publicationPk)
    }
})

onBeforeUnmount(() => {
    eventBus.off('patent-changed', handlePatentChanged)

    if (root) {
        root.dispose()
        root = null
    }

    debouncedRunProductMapping.cancel()
})

const handlePatentChanged = (newPatentId) => {
    resetComponentState()

    if (newPatentId) {
        checkCacheAndLoadData(newPatentId)
    }
}

const resetComponentState = () => {
    productList.value = []
    noProducts.value = false
    chartData.value = null
    targetItem.value = null

    if (root) {
        root.dispose()
        root = null
    }
}

watch(
    () => props.patentDetails,
    (newVal, oldVal) => {
        if (newVal?.publicationPk && newVal.publicationPk !== currentPatentId.value) {
            // Patent has changed
            currentPatentId.value = newVal.publicationPk

            // Reset component state
            resetComponentState()

            // Check cache for the new patent
            checkCacheAndLoadData(newVal.publicationPk)
        }
    },
    { immediate: true }
)

const onRunProductMapping = async () => {
    if (!props.patentDetails?.publicationPk) return

    // Determine the entityPk to use, in order of precedence
    const entityPk =
        playbookGenerationStore.playbookSelections[1]?.value ||
        reportGenerationStore.reportSelections[1]?.value ||
        patentViewerStore.entityPkList[0] ||
        arenaOutputStore.targetCompany[0].aonEntityPk ||
        entityStore.entity.aon_entity_pk

    debouncedRunProductMapping(props.patentDetails.publicationPk, entityPk)
}

const fetchProductMapping = async (patentId, entityPk) => {
    // If we already have cached data, use it
    if (patentViewerStore.mappedProductsExistInCache(patentId)) {
        const cachedData = patentViewerStore.getCachedMappedProducts(patentId)
        productList.value = cachedData
        if (productList.value.length === 0) {
            noProducts.value = true
        } else {
            mapDataToChart()
        }
        return
    }

    loadingProducts.value = true
    try {
        const { data } = await runProductMapping(patentId, entityPk)
        productList.value = data.slice(0, 5)

        // Cache the results
        patentViewerStore.setCachedMappedProducts(patentId, productList.value)

        if (productList.value.length === 0) {
            noProducts.value = true
            return
        }
    } catch (err) {
        logger.error(err)
        eventBus?.emit?.('snacktime', {
            type: 'error',
            message: 'Failed to map products from patent',
            contextual: true,
        })
        noProducts.value = true
    } finally {
        if (productList.value.length >= 1) {
            mapDataToChart()
        }
        loadingProducts.value = false
    }
}

const mapDataToChart = () => {
    if (productList.value.length <= 0) return

    chartData.value = productList.value.flatMap((product) => {
        // Map each year's filing data to the format expected by the violin chart
        return product.yearlyFilingMetrics.map((metric) => ({
            filingYear: metric.year,
            powerScore: metric.filingCount, // may need to genericize this - BB
            displayName: product.displayName,
        }))
    })

    const violinRow = productList.value.map((product) => ({
        displayName: product.displayName,
        color: '#1F589A',
    }))

    if (props.patentDetails && props.patentDetails.filingDate) {
        const filingDateMs =
            typeof props.patentDetails.filingDate === 'number'
                ? props.patentDetails.filingDate
                : new Date(props.patentDetails.filingDate).getTime()

        targetItem.value = {
            date: filingDateMs,
        }
    } else {
        targetItem.value = null
    }

    nextTick(() => {
        initializeChart(violinRow)
    })
}

const initializeChart = async (violinRow) => {
    if (root) {
        root.dispose()
        root = null
    }

    await nextTick()

    // Only initialize chart if the component is still mounted and we have data
    if (!chartData.value || chartData.value.length === 0) return

    try {
        am5.addLicense(config.license.AMChartsLicense)
        root = am5.Root.new('productMapChart')

        const violinChart = useViolinChart(root, violinRow, chartData.value)

        violinChart.configureViolin({ scrollContainer: true, snapToTicks: false })
        violinChart.createAllSeries({
            truncate: true,
            labelLength: 30,
            scoreLabel: 'Filing Count',
            targetItem: targetItem.value,
            snapToTicks: false,
        })
    } catch (err) {
        logger.error('Error initializing chart:', err)
    }
}

defineExpose({
    onRunProductMapping,
    productList,
    loadingProducts,
    noProducts,
})
</script>

<style scoped lang="scss">
@forward '@/styles/new_styles.scss';

.patent-mapped-products {
    min-height: 200px;
    position: relative;

    #productMapChart {
        width: 100%;
        /* Height is now dynamically set via :style binding */

        div {
            height: 100%;
        }
    }

    .legend {
        position: absolute;
        top: 20px;
        right: 20px;

        .target {
            .indicator {
                background-color: var(--color-gold);
                width: 5px;
                height: 25px;
            }
        }
    }
}
</style>
